import React, {useCallback, useEffect, useRef, useState} from 'react';

const MarkdownViewer = ({data, currentView, setCurrentView}) => {
    const containerRef = useRef(null)

    const parseContent = useCallback((layout) => {
        if (layout.layout_type === 'table') {
            return tableToHtml(layout.text)
        } else if (layout.layout_type === 'figure') {
            const tableRegex = /(<table[^>]*>[\s\S]*?<\/table>)/gim; // TODO // inner table breaks
            const figureRegex = /<figure[^>]*>\s*([\s\S]*?)\s*<\/figure>/gim;
            let figure = ''
            layout.text.replace(figureRegex, '$1').split(tableRegex).filter(part => part.trim()).forEach(part => {
                if (part.match(tableRegex)) figure += tableToHtml(part)
                else figure += markdownToHtml(part)
            })
            return figure
        } else { // layout.layout_type === 'text'
            return markdownToHtml(layout.text)
        }
    }, [])

    const markdownToHtml = (markdown) => {
        let html = markdown
            // tags as-is
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            // Headers
            .replace(/^##### (.*$)/gim, '<h5 class="text-sm font-semibold mt-4 mb-2">$1</h5>')
            .replace(/^#### (.*$)/gim, '<h4 class="text-md font-semibold mt-4 mb-2">$1</h4>')
            .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
            .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
            .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
            // Bold
            .replace(/\*\*(.*)\*\*/gim, '<strong class="font-bold">$1</strong>')
            // Italic
            .replace(/\*(.*)\*/gim, '<em class="italic">$1</em>')
            // Code inline
            .replace(/`([^`]*)`/gim, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
            // Links
            .replace(/\[([^\]]*)]\(([^)]*)\)/gim, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">$1</a>')
            // Line breaks
            .replace(/\n\n/gim, '</p><p class="mb-4">')
            // Single line breaks
            .replace(/\n/gim, '<br>')
            // Handle lists
            .replace(/^\* (.*)$/gim, '<li class="ml-4">• $1</li>')
            .replace(/^(\d+)\. (.*)$/gim, '<li class="ml-4">$1. $2</li>')

        // Wrap in paragraph if not already wrapped
        if (!html.startsWith('<h') && !html.startsWith('<p') && !html.startsWith('<li')) {
            html = `<p class="mb-2">${html}</p>`;
        }

        return html;
    };
    const tableToHtml = (table) => {
        return table
            .replace(/<table[^>]*>/gi, '<table class="w-full border-collapse border my-4 text-sm">')
            .replace(/<th([^>]*)>/gi, '<th$1 class="bg-gray-50 p-3 text-left font-semibold border border-gray-300">')
            .replace(/<td([^>]*)>/gi, '<td$1 class="p-3 border border-gray-300">')
            .replace(/<tr([^>]*)>/gi, '<tr$1 class="even:bg-gray-50 hover:bg-gray-100">');
    }
    const [markdowns, setMarkdowns] = useState([])
    useEffect(() => {
        const l = data.map(v => v['layouts']).flat()
        const m = data.map(v => ({'text': v['markdown']}))
        setMarkdowns(() => {
            if (l.length) {
                return l.map(v => v.map(layout => parseContent(layout)))
            } else if (m.length) {
                return m.map(v => [parseContent(v)])
            }
        })
    }, [data, parseContent])

    const onClick = useCallback((page, layout) => {
        if (currentView.page === page && currentView.layout === layout) setCurrentView({page: null, layout: null})
        else setCurrentView({page: page, layout: layout})
    }, [currentView, setCurrentView])

    useEffect(() => {
        if (currentView.page !== null && currentView.layout !== null && markdowns.length) {
            let flatIndex = 0;
            for (let i = 0; i < currentView.page; i++) {
                flatIndex += markdowns[i].length
            }
            flatIndex += currentView.layout
            containerRef.current.children[flatIndex]?.scrollIntoView({behavior: 'smooth'})
        }
    }, [currentView, markdowns]);

    return <div ref={containerRef} className="p-4">
        {markdowns.map((parts, pageIndex) => {
            return parts.map((part, layoutIndex) => {
                return <div key={layoutIndex}
                            className={`min-w-max rounded border-2 hover:border-blue-500 ${currentView.page === pageIndex && currentView.layout === layoutIndex ? 'border-blue-500' : 'border-transparent'}`}
                            onClick={() => onClick(pageIndex, layoutIndex)}
                            dangerouslySetInnerHTML={{__html: part}}/>
            })
        })}
    </div>
};

export default MarkdownViewer;
